import { useCallback, useEffect, useRef } from 'react';

/**
 * Makes the iOS back swipe close the top phone sheet.
 *
 * Opening a sheet pushes a history entry at the same URL, so a back swipe
 * lands on the page the sheet was opened over instead of leaving it. The
 * sheets are tracked on a stack of their own: several writers in the app
 * replace the current entry's state object (`replaceState({})`) while a sheet
 * can be on top, so `event.state` cannot be trusted to say which sheet an
 * entry belongs to.
 *
 * Module-level on purpose: one stack and one `popstate` listener for the whole
 * app, not one per hook instance (and not in an effect StrictMode runs twice).
 */
const stack = [];
// Resolvers for back navigations this module started (a close from the UI).
// Each consumes the next `popstate`, which must not close another sheet.
const pendingBacks = [];
// Sheets opened while such a back is still on its way (quick add opened in the
// same tap that closed the wizard or another sheet). Their entry is pushed
// once the browser has stepped back: pushed earlier, the step back, already
// aimed at the entry below, would land under it and leave the new sheet with
// no entry of its own.
const deferredPushes = [];
// Waiting for every such back to land (`sheetBacksSettled`).
const settledWaiters = [];
let listening = false;
// A back this module started resolves on its `popstate`. Should the browser
// never send one (a step past the start of the history), the back is taken as
// done after this long, so sheets waiting to push are not stuck behind it.
const BACK_TIMEOUT_MS = 1000;

// Marks the current task: every effect of one React commit runs inside it.
// A sheet opened in the same task that closes the sheet under it is a hand-off
// (quick add opened from the tap that closes the wizard), not a sheet nested
// in the one closing, so the close leaves it open.
let taskMark = null;
function currentTask() {
  if (!taskMark) {
    taskMark = {};
    Promise.resolve().then(() => { taskMark = null; });
  }
  return taskMark;
}

function pushEntry(entry) {
  entry.pushed = true;
  window.history.pushState({ mkSheet: entry.id }, '', window.location.href);
}

// A reload (or a workspace switch) with a sheet open lands on that sheet's
// history entry, which no sheet owns any more. Step back over it once, at
// load, so the first back swipe is not spent on it. A marked entry always has
// the URL of the entry below it (every writer that replaces the state drops
// the marker), so the page does not change.
if (typeof window !== 'undefined' && window.history.state?.mkSheet) {
  const step = () => {
    if (window.history.state?.mkSheet) window.history.back();
    else window.removeEventListener('popstate', step);
  };
  window.addEventListener('popstate', step);
  window.history.back();
}

function settleBack(resolver) {
  const i = pendingBacks.indexOf(resolver);
  if (i < 0) return;
  pendingBacks.splice(i, 1);
  resolver();
  if (!pendingBacks.length) {
    deferredPushes.splice(0).forEach(pushEntry);
    settledWaiters.splice(0).forEach((resolve) => resolve());
  }
}

function onPopState() {
  if (pendingBacks.length) {
    settleBack(pendingBacks[0]);
    return;
  }
  const top = stack[stack.length - 1];
  if (!top) {
    // An entry no open sheet owns: one a sheet left ahead in the history when
    // it closed, reached with forward (after a replace navigation, the only
    // thing ahead of Movements is the onboarding screen that sheet was over).
    // Step back off it, as the load-time check above does.
    if (window.history.state?.mkSheet) window.history.back();
    return;
  }
  // A back that left the page the sheets were opened over (several steps at
  // once, from the long-press back list): every sheet goes, and none gets its
  // entry back, which would land on top of another page.
  if (window.location.href !== top.href) {
    const closing = stack.splice(0).reverse();
    closing.forEach((entry) => entry.options.current.onPop?.());
    return;
  }
  // By the time `popstate` fires the entry is gone, so a sheet with unsaved
  // edits cannot veto the swipe. It gets its entry back and asks instead.
  if (top.options.current.isDirty?.()) {
    window.history.pushState({ mkSheet: top.id }, '', window.location.href);
    top.options.current.onDirtyPop?.();
    return;
  }
  stack.pop();
  top.options.current.onPop?.();
}

function ensureListener() {
  if (listening || typeof window === 'undefined') return;
  window.addEventListener('popstate', onPopState);
  listening = true;
}

function removeEntry(entry) {
  const i = stack.indexOf(entry);
  if (i >= 0) stack.splice(i, 1);
  const d = deferredPushes.indexOf(entry);
  if (d >= 0) deferredPushes.splice(d, 1);
}

/** True while any phone sheet holds a history entry. */
export function isSheetOpen() {
  return stack.length > 0;
}

/**
 * Resolves once every back a closing sheet started has landed; at once when
 * none is on its way (a back swipe has already landed). A page that leaves
 * when its sheet closes navigates after it: a navigation made before that
 * `popstate` would be undone by it.
 */
export function sheetBacksSettled() {
  if (!pendingBacks.length) return Promise.resolve();
  return new Promise((resolve) => { settledWaiters.push(resolve); });
}

/**
 * Close `entry` from the UI: step back over its history entry and resolve once
 * the browser has done so. A navigation made before that `popstate` would be
 * reverted by it, which is why callers await this before navigating.
 *
 * Sheets stacked on top of it (a picker over the sheet, a list pushed inside
 * it) close with it, in the same step back. A sheet opened in this same task
 * is a hand-off, not one of those: it stays open, and its entry is pushed
 * again once the browser has stepped back.
 */
function closeEntry(entry) {
  const i = stack.indexOf(entry);
  if (i < 0) return Promise.resolve();
  const removed = stack.splice(i);
  const task = currentTask();
  const spared = removed.filter((e, k) => k > 0 && e.openedIn === task);
  const closing = removed.filter((e) => !spared.includes(e));
  closing.slice(1).forEach((above) => above.options.current.onPop?.());
  // An entry still waiting for its push has nothing to step back over.
  closing.forEach((e) => {
    const d = deferredPushes.indexOf(e);
    if (d >= 0) deferredPushes.splice(d, 1);
  });
  const steps = removed.filter((e) => e.pushed).length;
  spared.forEach((e) => {
    stack.push(e);
    if (e.pushed) {
      e.pushed = false;
      deferredPushes.push(e);
    }
  });
  if (!steps) return Promise.resolve();
  return new Promise((resolve) => {
    let timer = null;
    const done = () => {
      clearTimeout(timer);
      resolve();
    };
    pendingBacks.push(done);
    timer = setTimeout(() => settleBack(done), BACK_TIMEOUT_MS);
    window.history.go(-steps);
  });
}

/**
 * @param {string} id - names the sheet in its history entry (`{ mkSheet: id }`)
 * @param {boolean} isOpen
 * @param {{ onPop?: Function, isDirty?: Function, onDirtyPop?: Function }} options
 *   `onPop` closes the sheet after a back swipe; `isDirty` and `onDirtyPop`
 *   turn a back swipe on a sheet with unsaved edits into a question.
 * @returns {{ close(): Promise<void>, isTop(): boolean }}
 *   `isTop` says whether this sheet is the one on top, the one a back swipe
 *   (or Escape) would close.
 */
export default function useSheetHistory(id, isOpen, { onPop, isDirty, onDirtyPop } = {}) {
  const options = useRef({ onPop, isDirty, onDirtyPop });
  options.current = { onPop, isDirty, onDirtyPop };
  const entryRef = useRef(null);

  useEffect(() => {
    ensureListener();
    if (!isOpen) {
      // Closed by its owner without close(): step back over the entry so it
      // does not linger behind the page.
      const entry = entryRef.current;
      entryRef.current = null;
      if (entry) closeEntry(entry);
      return undefined;
    }
    let entry = entryRef.current;
    // The same sheet, still open, remounting elsewhere in the tree (crossing
    // the phone width moves Settings from the desktop layout to the phone
    // one): it takes over the entry the old instance left, instead of
    // pushing a second one the back swipe would then have to step over.
    const orphan = !entry && stack[stack.length - 1];
    if (orphan && orphan.detached && orphan.id === id) {
      entry = orphan;
      entry.options = options;
      entryRef.current = entry;
    }
    if (entry) {
      // StrictMode's second run: the entry was never really left.
      entry.detached = false;
      if (!stack.includes(entry)) stack.push(entry);
    } else {
      // `href` is the URL of the entry below, where a back from this sheet
      // lands.
      entry = {
        id, options, detached: false, pushed: false, href: window.location.href, openedIn: currentTask(),
      };
      entryRef.current = entry;
      stack.push(entry);
      if (pendingBacks.length) deferredPushes.push(entry);
      else pushEntry(entry);
    }
    return () => {
      // Deferred, so StrictMode's unmount-and-remount keeps the same entry.
      // A sheet that really unmounts without close() leaves the stack, and,
      // when its entry is still the one showing, the history too, so no back
      // swipe is spent on it. Its entry is not showing after a navigation,
      // which must not be undone.
      entry.detached = true;
      setTimeout(() => {
        if (!entry.detached) return;
        if (entryRef.current === entry) entryRef.current = null;
        const showing = stack[stack.length - 1] === entry && entry.pushed
          && window.location.href === entry.href;
        if (showing) closeEntry(entry);
        else removeEntry(entry);
      }, 0);
    };
  }, [id, isOpen]);

  const close = useCallback(() => {
    const entry = entryRef.current;
    if (!entry) return Promise.resolve();
    entryRef.current = null;
    const done = closeEntry(entry);
    options.current.onPop?.();
    return done;
  }, []);

  const isTop = useCallback(() => {
    const entry = entryRef.current;
    return Boolean(entry) && stack[stack.length - 1] === entry;
  }, []);

  return { close, isTop };
}
